import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiFetch, apiJson } from '@/lib/apiClient';
import type { CheckResult } from '@/features/provisioning/useProvisioning';
import { adminVehiclesQueryKey } from '@/features/vehicles/useAdminVehicles';

/**
 * The install record (slice 30).
 *
 * Types mirror the wire contract in `api/src/modules/admin/routes.ts`
 * (`InstallationZ`, the list row, the relocation chain) and are never
 * re-declared from the screen's point of view.
 *
 * 🔴 NO `imei` ANYWHERE ON THESE WIRES. The chain and list endpoints are the ones
 * most likely to join through to `devices.imei`, and the server deliberately
 * leaves it off (AC-8 scans the chain body for the fixture IMEI). The tracker is
 * identified by MODEL on the list, and by the vehicle plate on the chain.
 */

export type SystemVoltage = 12 | 24;

export interface Installation {
  id: string;
  deviceAssignmentId: string;
  vehicleId: string;
  vehiclePlate: string;
  installerName: string;
  installerPhone: string | null;
  systemVoltage: SystemVoltage;
  placement: string;
  relayFitted: boolean;
  /** Non-null iff a relay is fitted — the DB CHECK equivalence. */
  flybackDiodeFitted: boolean | null;
  inlineFuseFitted: boolean | null;
  tamperSealId: string | null;
  notes: string | null;
  recordedByStaffId: string;
  installedAt: string;
  /** The open assignment was closed after this record was written — read-only. */
  assignmentClosed: boolean;
  /** Server-stamped only, by confirm-live. Non-null = confirmed. */
  verifiedLiveAt: string | null;
  verifiedByStaffId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The reason a record is absent, when it is. `null` when one is present. */
export type NoRecordReason = 'no_tracker' | 'no_record' | null;

export interface GetInstallationResponse {
  installation: Installation | null;
  reason: NoRecordReason;
}

/**
 * The create/patch body. `flybackDiodeFitted` / `inlineFuseFitted` are REQUIRED
 * iff `relayFitted`, FORBIDDEN otherwise — the server refuses the mismatch (the
 * DB CHECK), so the panel builds this shape from the relay toggle rather than
 * letting the two drift.
 */
export interface InstallationWriteBody {
  installerName: string;
  /** `string` to set, `null` to clear on a PATCH; omitted on create. */
  installerPhone?: string | null;
  systemVoltage: SystemVoltage;
  placement: string;
  relayFitted: boolean;
  flybackDiodeFitted?: boolean | null;
  inlineFuseFitted?: boolean | null;
  tamperSealId?: string | null;
  notes?: string | null;
}

export interface ConfirmLiveResponse {
  installation: Installation;
  /** True when it was ALREADY confirmed — 200 with the original instant. */
  alreadyConfirmed: boolean;
}

export interface InstallationChainEntry {
  assignmentId: string;
  vehicleId: string;
  vehiclePlate: string;
  fromAt: string;
  toAt: string | null;
  installation: Installation | null;
}

export interface InstallationChainResponse {
  deviceId: string;
  chain: InstallationChainEntry[];
}

export interface InstallationListRow {
  id: string;
  vehicleId: string;
  vehiclePlate: string;
  tenantId: string;
  tenantName: string;
  installerName: string;
  installerPhone: string | null;
  /** Tracker MODEL, never the IMEI. */
  trackerModel: string | null;
  installedAt: string;
  systemVoltage: number;
  placement: string;
  relayFitted: boolean;
  status: 'confirmed' | 'unconfirmed';
  verifiedLiveAt: string | null;
  recordedAt: string;
}

export interface InstallationListResponse {
  installations: InstallationListRow[];
}

export interface InstallationListFilters {
  tenantId?: string;
  installer?: string;
  from?: string;
  to?: string;
}

export const vehicleInstallationKey = (vehicleId: string) =>
  ['admin', 'installation', 'vehicle', vehicleId] as const;
export const installationChainKey = (deviceId: string) =>
  ['admin', 'installation', 'chain', deviceId] as const;
export const installationsListKey = (filters: InstallationListFilters) =>
  ['admin', 'installations', filters] as const;

function filtersToQuery(filters: InstallationListFilters): string {
  const qs = new URLSearchParams();
  if (filters.tenantId) qs.set('tenantId', filters.tenantId);
  if (filters.installer) qs.set('installer', filters.installer);
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** The record for a vehicle's open assignment, or the reason it is absent. */
export function useVehicleInstallation(
  vehicleId: string,
): UseQueryResult<GetInstallationResponse> {
  return useQuery({
    queryKey: vehicleInstallationKey(vehicleId),
    queryFn: ({ signal }) =>
      apiJson<GetInstallationResponse>(`/api/admin/vehicles/${vehicleId}/installation`, { signal }),
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

export function useCreateInstallation(
  vehicleId: string,
): UseMutationResult<{ installation: Installation }, Error, InstallationWriteBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InstallationWriteBody) =>
      apiJson<{ installation: Installation }>(`/api/admin/vehicles/${vehicleId}/installation`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vehicleInstallationKey(vehicleId) });
      // The vehicles list carries `installStatus`; a fresh record moves it from
      // "none" to "unconfirmed", so its chip must not go stale.
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
    },
  });
}

export function useUpdateInstallation(
  vehicleId: string,
): UseMutationResult<{ installation: Installation }, Error, { id: string; body: InstallationWriteBody }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiJson<{ installation: Installation }>(`/api/admin/installations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vehicleInstallationKey(vehicleId) });
    },
  });
}

/**
 * Confirm-live: the server runs the device checks and stamps `verified_live_at`
 * only when they all pass. On failure it answers 409 with the failing checks.
 *
 * 🔴 AFTER A SUCCESSFUL CONFIRM, THE VEHICLE'S go-live checks MUST BE RE-RUNNABLE
 * to pick up `install_confirmed`. `GoLivePanel` runs those as a mutation and
 * always offers "Re-check", so the operator re-runs it; here we invalidate the
 * vehicles list so the `installStatus` chip flips to "confirmed" without a reload.
 */
export function useConfirmLive(
  vehicleId: string,
): UseMutationResult<ConfirmLiveResponse, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<ConfirmLiveResponse>(`/api/admin/installations/${id}/confirm-live`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vehicleInstallationKey(vehicleId) });
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
    },
  });
}

/** The relocation chain for a tracker — every assignment, newest first. */
export function useInstallationChain(
  deviceId: string,
): UseQueryResult<InstallationChainResponse> {
  return useQuery({
    queryKey: installationChainKey(deviceId),
    queryFn: ({ signal }) =>
      apiJson<InstallationChainResponse>(`/api/admin/devices/${deviceId}/installations`, { signal }),
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

/** The staff list (canvas 3d left table). */
export function useInstallationsList(
  filters: InstallationListFilters,
): UseQueryResult<InstallationListResponse> {
  return useQuery({
    queryKey: installationsListKey(filters),
    queryFn: ({ signal }) =>
      apiJson<InstallationListResponse>(`/api/admin/installations${filtersToQuery(filters)}`, {
        signal,
      }),
    retry: (count, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
  });
}

const ROW_COUNT_HEADER = 'X-Report-Row-Count';

/**
 * 🔴 QUOTE-AWARE, AND A NAIVE `split('\n')` IS WRONG HERE. This is the same
 * counter `useTenantExport.ts` documents at length: the server quotes any field
 * with a comma, quote or CR/LF, so a placement or note may carry an embedded
 * newline inside a quoted field. Counting lines would over-count and refuse a
 * COMPLETE file as truncated. Duplicated (not imported) only because it is a
 * one-file helper; if the server serialiser changes, both twins change.
 */
export function countCsvDataRows(text: string): number {
  let records = 0;
  let inQuotes = false;
  let sawContent = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') i++;
        else inQuotes = false;
      }
      sawContent = true;
    } else if (ch === '"') {
      inQuotes = true;
      sawContent = true;
    } else if (ch === '\n') {
      records++;
      sawContent = false;
    } else if (ch !== '\r') {
      sawContent = true;
    }
  }
  if (sawContent) records++;
  return Math.max(0, records - 1);
}

function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download the install register as CSV, same filters as the list.
 *
 * 🔴 THE TRUNCATION CHECK IS NOT OPTIONAL — the same contract the tenant export
 * follows. A page query that fails mid-stream closes the body cleanly, so a
 * SHORT file resolves and looks whole; the server sends the exact row count in a
 * header and we refuse any file whose decoded count differs.
 */
export async function downloadInstallationsExport(
  filters: InstallationListFilters,
): Promise<void> {
  const res = await apiFetch(`/api/admin/installations/export.csv${filtersToQuery(filters)}`);
  if (!res.ok) throw new ApiError(res.status, { error: `admin_export_failed_${res.status}` });

  const blob = await res.blob();
  const expectedRaw = res.headers.get(ROW_COUNT_HEADER);
  if (expectedRaw !== null) {
    const expected = Number(expectedRaw);
    const actual = countCsvDataRows(await blob.text());
    if (!Number.isFinite(expected) || actual !== expected) {
      throw new ApiError(res.status, {
        error: 'admin_export_truncated',
        expected: expectedRaw,
        got: actual,
      });
    }
  }
  saveBlob('raad-installations.csv', blob);
}

/**
 * Pull the failing checks out of a `409 checks_failed` body. Shape-checked, never
 * cast: an unexpected body must not throw during render and take the portal down.
 * The twin of `GoLivePanel`'s local helper.
 */
export function failingChecksFrom(err: unknown): CheckResult[] | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (body === null || typeof body !== 'object') return null;
  const b = body as { error?: unknown; checks?: unknown };
  if (b.error !== 'checks_failed' || !Array.isArray(b.checks)) return null;
  const checks = b.checks.filter(
    (c): c is CheckResult =>
      c !== null &&
      typeof c === 'object' &&
      typeof (c as CheckResult).id === 'string' &&
      typeof (c as CheckResult).detail === 'string',
  );
  return checks.length > 0 ? checks : null;
}
